<template>
  <div>
    <!-- Job Detail Modal -->
    <v-dialog v-model="show_details" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          Job Details
          <v-spacer></v-spacer>
          <v-btn icon @click="show_details = false">
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="loadingJobDetail" class="text-center py-8">
          <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
        </v-card-text>
        <v-card-text v-else-if="jobDetail">
          <v-row>
            <v-col cols="12" md="6">
              <p><strong>Job ID:</strong> {{ jobDetail.id }}</p>
              <p><strong>Queue:</strong> {{ jobDetail.queue }}</p>
              <p><strong>Job Class:</strong> {{ jobDetail.job_class || 'N/A' }}</p>
              <p><strong>Job UUID:</strong> {{ jobDetail.job_uuid || 'N/A' }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <p><strong>Shop:</strong> {{ jobDetail.shop_name || 'N/A' }}</p>
              <p><strong>Attribute ID:</strong> {{ jobDetail.attribute_id || 'N/A' }}</p>
              <p><strong>Attempts:</strong> {{ jobDetail.attempts }}</p>
              <p><strong>Status:</strong>
                <v-chip small :color="getStatusColor(jobDetail.reserved_at)" class="ml-2" text-color="white">
                  {{ getStatus(jobDetail.reserved_at) }}
                </v-chip>
              </p>
            </v-col>
            <v-col cols="12" md="6">
              <p><strong>Created At:</strong> {{ formatDate(jobDetail.created_at) }}</p>
              <p><strong>Available At:</strong> {{ formatDate(jobDetail.available_at) }}</p>
              <p v-if="jobDetail.reserved_at"><strong>Reserved At:</strong> {{ formatDate(jobDetail.reserved_at) }}</p>
            </v-col>
          </v-row>
          <v-divider class="my-4"></v-divider>
          <h3 class="mb-2">Payload (Decoded)</h3>
          <pre style="padding: 20px; background: #120f12; color: #fff; border-radius: 10px; max-height: 400px; overflow: auto">
            {{ JSON.stringify(jobDetail.payload_decoded || {}, null, 2) }}
          </pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="show_details = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container>
      <div class="d-flex justify-space-between align-center mb-4">
        <h2 class="text-h5">Application Queue</h2>
        <v-btn color="primary" @click="fetchStatistics" :loading="loadingStats" small>
          <v-icon left small>{{ icons.mdiRefresh }}</v-icon>
          Refresh Stats
        </v-btn>
      </div>

      <!-- Statistics Cards -->
      <v-row class="mb-4" v-if="statistics">
        <v-col cols="12" sm="6" md="3">
          <v-card class="text-center pa-4" color="primary" dark>
            <v-icon large class="mb-2">{{ icons.mdiBriefcase }}</v-icon>
            <h3 class="text-h4">{{ statistics.total_jobs || 0 }}</h3>
            <p class="mb-0">Total Jobs</p>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="text-center pa-4" color="warning" dark>
            <v-icon large class="mb-2">{{ icons.mdiClockOutline }}</v-icon>
            <h3 class="text-h4">{{ statistics.pending_jobs || 0 }}</h3>
            <p class="mb-0">Pending</p>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="text-center pa-4" color="info" dark>
            <v-icon large class="mb-2">{{ icons.mdiPlayCircle }}</v-icon>
            <h3 class="text-h4">{{ statistics.reserved_jobs || 0 }}</h3>
            <p class="mb-0">Reserved</p>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="text-center pa-4" color="success" dark>
            <v-icon large class="mb-2">{{ icons.mdiCog }}</v-icon>
            <h3 class="text-h4">{{ (statistics.jobs_by_status && statistics.jobs_by_status.processing) || 0 }}</h3>
            <p class="mb-0">Processing</p>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tabs for different views -->
      <v-tabs v-model="activeTab" class="mb-4">
        <v-tab href="#tab-jobs">Jobs List</v-tab>
        <v-tab href="#tab-by-shop">Counts by Shop</v-tab>
        <v-tab href="#tab-by-queue">Counts by Queue</v-tab>
        <v-tab href="#tab-breakdown">Shop-Queue Breakdown</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Jobs List Tab -->
        <v-tab-item value="tab-jobs">
          <v-card>
            <v-card-title>
              <v-row class="align-center">
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filters.shop_id"
                    label="Shop ID"
                    type="number"
                    outlined
                    dense
                    hide-details
                    clearable
                    @change="fetchJobs"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.queue"
                    :items="queueOptions"
                    label="Queue"
                    outlined
                    dense
                    hide-details
                    clearable
                    @change="fetchJobs"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="2">
                  <v-select
                    v-model="filters.status"
                    :items="statusOptions"
                    label="Status"
                    outlined
                    dense
                    hide-details
                    clearable
                    @change="fetchJobs"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="filters.date_from"
                    label="Date From"
                    type="date"
                    outlined
                    dense
                    hide-details
                    @change="fetchJobs"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="filters.date_to"
                    label="Date To"
                    type="date"
                    outlined
                    dense
                    hide-details
                    @change="fetchJobs"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filters.attribute_id"
                    label="Attribute ID"
                    outlined
                    dense
                    hide-details
                    clearable
                    @change="fetchJobs"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="filters.min_attempts"
                    label="Min Attempts"
                    type="number"
                    outlined
                    dense
                    hide-details
                    clearable
                    @change="fetchJobs"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="filters.max_attempts"
                    label="Max Attempts"
                    type="number"
                    outlined
                    dense
                    hide-details
                    clearable
                    @change="fetchJobs"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="filters.include_shopifysync"
                    label="Include Shopify Sync"
                    hide-details
                    @change="fetchJobs"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" md="2">
                  <v-btn color="primary" @click="fetchJobs" :loading="loadingJobs" block>
                    <v-icon left small>{{ icons.mdiMagnify }}</v-icon>
                    Search
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-title>
            <v-data-table
              :headers="jobHeaders"
              :items="jobs"
              :items-per-page="filters.per_page || 50"
              :loading="loadingJobs"
              class="elevation-1"
              hide-default-footer
            >
              <template v-slot:body="{ items }">
                <tbody v-if="!loadingJobs">
                  <tr v-if="!items.length">
                    <td colspan="8" class="text-center" style="padding: 20px 0px">
                      <img :src="require('@/assets/images/misc/no-data.png')" alt="" width="250px" />
                      <p style="font-size: 25px">No Data Found!!</p>
                    </td>
                  </tr>
                  <tr v-else v-for="item in items" :key="item.id">
                    <td>{{ item.id }}</td>
                    <td>
                      <v-chip small color="info" text-color="white">{{ item.queue }}</v-chip>
                    </td>
                    <td>{{ item.shop_name || 'N/A' }}</td>
                    <td>{{ item.attribute_id || 'N/A' }}</td>
                    <td>
                      <v-chip small :color="getStatusColor(item.reserved_at)" text-color="white">
                        {{ getStatus(item.reserved_at) }}
                      </v-chip>
                    </td>
                    <td>{{ item.attempts }}</td>
                    <td>{{ formatDate(item.created_at) }}</td>
                    <td>
                      <v-btn @click="showJobDetail(item.id)" fab small color="primary" class="table-action--button">
                        <v-icon small dark>{{ icons.mdiEye }}</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-data-table>
            <v-pagination
              v-if="pagination && pagination.last_page > 1"
              v-model="currentPage"
              :length="pagination.last_page"
              :total-visible="7"
              class="my-4"
              @input="handlePageChange"
            ></v-pagination>
          </v-card>
        </v-tab-item>

        <!-- Counts by Shop Tab -->
        <v-tab-item value="tab-by-shop">
          <v-card>
            <v-card-title>
              <v-row>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="shopCountFilters.queue"
                    :items="queueOptions"
                    label="Filter by Queue"
                    outlined
                    dense
                    hide-details
                    clearable
                    @change="fetchCountsByShop"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="shopCountFilters.include_shopifysync"
                    label="Include Shopify Sync"
                    hide-details
                    @change="fetchCountsByShop"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-card-title>
            <v-data-table
              :headers="shopCountHeaders"
              :items="countsByShop"
              :loading="loadingShopCounts"
              class="elevation-1"
            >
              <template v-slot:body="{ items }">
                <tbody v-if="!loadingShopCounts">
                  <tr v-if="!items.length">
                    <td colspan="3" class="text-center" style="padding: 20px 0px">
                      <img :src="require('@/assets/images/misc/no-data.png')" alt="" width="250px" />
                      <p style="font-size: 25px">No Data Found!!</p>
                    </td>
                  </tr>
                  <tr v-else v-for="item in items" :key="item.shop_id">
                    <td>{{ item.shop_id }}</td>
                    <td>{{ item.shop_name || 'N/A' }}</td>
                    <td>
                      <v-chip color="primary" text-color="white">{{ item.job_count }}</v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-data-table>
            <v-card-text v-if="shopCountSummary" class="text-right">
              <strong>Total Shops: {{ shopCountSummary.total_shops }}</strong> |
              <strong>Total Jobs: {{ shopCountSummary.total_jobs }}</strong>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Counts by Queue Tab -->
        <v-tab-item value="tab-by-queue">
          <v-card>
            <v-card-title>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="queueCountFilters.shop_id"
                    label="Filter by Shop ID"
                    type="number"
                    outlined
                    dense
                    hide-details
                    clearable
                    @change="fetchCountsByQueue"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="queueCountFilters.include_shopifysync"
                    label="Include Shopify Sync"
                    hide-details
                    @change="fetchCountsByQueue"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-card-title>
            <v-data-table
              :headers="queueCountHeaders"
              :items="countsByQueue"
              :loading="loadingQueueCounts"
              class="elevation-1"
            >
              <template v-slot:body="{ items }">
                <tbody v-if="!loadingQueueCounts">
                  <tr v-if="!items.length">
                    <td colspan="2" class="text-center" style="padding: 20px 0px">
                      <img :src="require('@/assets/images/misc/no-data.png')" alt="" width="250px" />
                      <p style="font-size: 25px">No Data Found!!</p>
                    </td>
                  </tr>
                  <tr v-else v-for="item in items" :key="item.queue">
                    <td>
                      <v-chip small color="info" text-color="white">{{ item.queue }}</v-chip>
                    </td>
                    <td>
                      <v-chip color="primary" text-color="white">{{ item.job_count }}</v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-data-table>
            <v-card-text v-if="queueCountSummary" class="text-right">
              <strong>Total Queues: {{ queueCountSummary.total_queues }}</strong> |
              <strong>Total Jobs: {{ queueCountSummary.total_jobs }}</strong>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Shop-Queue Breakdown Tab -->
        <v-tab-item value="tab-breakdown">
          <v-card>
            <v-card-title>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="breakdownFilters.shop_id"
                    label="Filter by Shop ID"
                    type="number"
                    outlined
                    dense
                    hide-details
                    clearable
                    @change="fetchBreakdown"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="breakdownFilters.queue"
                    :items="queueOptions"
                    label="Filter by Queue"
                    outlined
                    dense
                    hide-details
                    clearable
                    @change="fetchBreakdown"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-checkbox
                    v-model="breakdownFilters.include_shopifysync"
                    label="Include Shopify Sync"
                    hide-details
                    @change="fetchBreakdown"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-card-title>
            <v-expansion-panels>
              <v-expansion-panel v-for="shop in breakdown" :key="shop.shop_id">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <div>
                      <strong>{{ shop.shop_name || `Shop ID: ${shop.shop_id}` }}</strong>
                      <v-chip small class="ml-2" color="primary" text-color="white">
                        Total: {{ shop.total_jobs }}
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>Queue</th>
                          <th class="text-right">Job Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="queue in shop.queues" :key="queue.queue">
                          <td>
                            <v-chip small color="info" text-color="white">{{ queue.queue }}</v-chip>
                          </td>
                          <td class="text-right">
                            <v-chip color="primary" text-color="white">{{ queue.job_count }}</v-chip>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <v-card-text v-if="breakdownSummary" class="text-right mt-4">
              <strong>Total Shops: {{ breakdownSummary.total_shops }}</strong> |
              <strong>Total Jobs: {{ breakdownSummary.total_jobs }}</strong>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-container>
  </div>
</template>

<script>
import {
  mdiEye,
  mdiClose,
  mdiRefresh,
  mdiBriefcase,
  mdiClockOutline,
  mdiPlayCircle,
  mdiCog,
  mdiMagnify,
} from '@mdi/js'

export default {
  data() {
    return {
      activeTab: 'tab-jobs',
      loadingJobs: false,
      loadingStats: false,
      loadingShopCounts: false,
      loadingQueueCounts: false,
      loadingBreakdown: false,
      loadingJobDetail: false,
      show_details: false,
      jobDetail: null,
      statistics: null,
      jobs: [],
      countsByShop: [],
      countsByQueue: [],
      breakdown: [],
      pagination: null,
      currentPage: 1,
      filters: {
        shop_id: null,
        queue: null,
        attribute_id: null,
        status: null,
        min_attempts: null,
        max_attempts: null,
        date_from: null,
        date_to: null,
        include_shopifysync: false,
        per_page: 50,
        page: 1,
      },
      shopCountFilters: {
        queue: null,
        include_shopifysync: false,
      },
      queueCountFilters: {
        shop_id: null,
        include_shopifysync: false,
      },
      breakdownFilters: {
        shop_id: null,
        queue: null,
        include_shopifysync: false,
      },
      queueOptions: [
        'product-auto-upload',
        'inventory-sync-job',
        'product-feed-upload',
        'product-image-upload',
        'create-on-shopify',
        'default',
        'image-sync',
        'video-fetch-job',
        'property-sync'
      ],
      statusOptions: ['pending', 'reserved', 'processing'],
      jobHeaders: [
        { text: 'ID', value: 'id' },
        { text: 'Queue', value: 'queue' },
        { text: 'Shop', value: 'shop_name' },
        { text: 'Attribute ID', value: 'attribute_id' },
        { text: 'Status', value: 'status' },
        { text: 'Attempts', value: 'attempts' },
        { text: 'Created At', value: 'created_at' },
        { text: 'Actions', value: 'actions' },
      ],
      shopCountHeaders: [
        { text: 'Shop ID', value: 'shop_id' },
        { text: 'Shop Name', value: 'shop_name' },
        { text: 'Job Count', value: 'job_count' },
      ],
      queueCountHeaders: [
        { text: 'Queue', value: 'queue' },
        { text: 'Job Count', value: 'job_count' },
      ],
      shopCountSummary: null,
      queueCountSummary: null,
      breakdownSummary: null,
      icons: {
        mdiEye: mdiEye,
        mdiClose: mdiClose,
        mdiRefresh: mdiRefresh,
        mdiBriefcase: mdiBriefcase,
        mdiClockOutline: mdiClockOutline,
        mdiPlayCircle: mdiPlayCircle,
        mdiCog: mdiCog,
        mdiMagnify: mdiMagnify,
      },
      interval: null,
    }
  },
  mounted() {
    this.fetchStatistics()
    this.fetchJobs()
    this.fetchCountsByShop()
    this.fetchCountsByQueue()
    this.fetchBreakdown()
    // Auto-refresh every 30 seconds
    this.interval = setInterval(() => {
      if (this.activeTab === 'tab-jobs') {
        this.fetchJobs()
      }
      this.fetchStatistics()
    }, 30000)
  },
  destroyed() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  },
  methods: {
    async fetchStatistics() {
      try {
        this.loadingStats = true
        const params = {
          shop_id: this.filters.shop_id,
          queue: this.filters.queue,
          include_shopifysync: this.filters.include_shopifysync,
        }
        const response = await axios.get('/admin/jobs/statistics', { params })
        if (response.data.success) {
          this.statistics = response.data.data
        }
      } catch (e) {
        console.error('Error fetching statistics:', e)
      } finally {
        this.loadingStats = false
      }
    },
    async fetchJobs() {
      try {
        this.loadingJobs = true
        const params = { ...this.filters, page: this.currentPage }
        // Remove null/empty values
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === '') {
            delete params[key]
          }
        })
        const response = await axios.get('/admin/jobs', { params })
        if (response.data.success) {
          this.jobs = response.data.data || []
          this.pagination = response.data.pagination
        }
      } catch (e) {
        console.error('Error fetching jobs:', e)
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch jobs',
        })
      } finally {
        this.loadingJobs = false
      }
    },
    async fetchCountsByShop() {
      try {
        this.loadingShopCounts = true
        const params = { ...this.shopCountFilters }
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === '') {
            delete params[key]
          }
        })
        const response = await axios.get('/admin/jobs/counts/by-shop', { params })
        if (response.data.success) {
          this.countsByShop = response.data.data || []
          this.shopCountSummary = {
            total_shops: response.data.total_shops,
            total_jobs: response.data.total_jobs,
          }
        }
      } catch (e) {
        console.error('Error fetching counts by shop:', e)
      } finally {
        this.loadingShopCounts = false
      }
    },
    async fetchCountsByQueue() {
      try {
        this.loadingQueueCounts = true
        const params = { ...this.queueCountFilters }
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === '') {
            delete params[key]
          }
        })
        const response = await axios.get('/admin/jobs/counts/by-queue', { params })
        if (response.data.success) {
          this.countsByQueue = response.data.data || []
          this.queueCountSummary = {
            total_queues: response.data.total_queues,
            total_jobs: response.data.total_jobs,
          }
        }
      } catch (e) {
        console.error('Error fetching counts by queue:', e)
      } finally {
        this.loadingQueueCounts = false
      }
    },
    async fetchBreakdown() {
      try {
        this.loadingBreakdown = true
        const params = { ...this.breakdownFilters }
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === '') {
            delete params[key]
          }
        })
        const response = await axios.get('/admin/jobs/counts/by-shop-queue', { params })
        if (response.data.success) {
          this.breakdown = response.data.data || []
          this.breakdownSummary = {
            total_shops: response.data.total_shops,
            total_jobs: response.data.total_jobs,
          }
        }
      } catch (e) {
        console.error('Error fetching breakdown:', e)
      } finally {
        this.loadingBreakdown = false
      }
    },
    async showJobDetail(jobId) {
      try {
        this.show_details = true
        this.loadingJobDetail = true
        this.jobDetail = null
        const response = await axios.get(`/admin/jobs/${jobId}`)
        if (response.data.success) {
          this.jobDetail = response.data.data
        }
      } catch (e) {
        console.error('Error fetching job detail:', e)
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch job details',
        })
      } finally {
        this.loadingJobDetail = false
      }
    },
    handlePageChange(page) {
      this.currentPage = page
      this.fetchJobs()
    },
    getStatus(reserved_at) {
      if (!reserved_at) return 'Pending'
      return 'Reserved'
    },
    getStatusColor(reserved_at) {
      if (!reserved_at) return 'warning'
      return 'info'
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString()
    },
  },
  watch: {
    activeTab() {
      // Fetch data when switching tabs
      if (this.activeTab === 'tab-by-shop') {
        this.fetchCountsByShop()
      } else if (this.activeTab === 'tab-by-queue') {
        this.fetchCountsByQueue()
      } else if (this.activeTab === 'tab-breakdown') {
        this.fetchBreakdown()
      }
    },
  },
}
</script>

<style scoped>
.table-action--button {
  margin: 0 2px;
}
</style>
